/**
 * Checks on what a stranger types into the two public forms that send email
 * (ERI-1559, ERI-1618). send-contact-email sends an auto-reply, and
 * create-shared-calendar sends the calendar link, to whatever address is in
 * the request. Both refuse a bad request with a 400 before anything is
 * stored or sent.
 *
 * The same file is at supabase/functions/_shared/input-checks.ts (the edge
 * functions) and src/utils/inputChecks.ts (the contact form), so the form
 * holds visitors to the same limits and an honest one never sees the 400.
 * No imports, so the two copies stay identical (a test checks).
 */

/** RFC 5321 allows 254 characters for an address. */
export const EMAIL_MAX = 254;
/** RFC 5321 allows 64 characters before the @. */
const EMAIL_LOCAL_MAX = 64;

/**
 * Contact form caps. A name rarely runs past 40 characters. The support
 * email's subject line is already cut at 150. 5,000 characters is about 800
 * words, well past any contact message the form has had.
 */
export const CONTACT_LIMITS = { name: 100, subject: 150, message: 5000 } as const;

/**
 * Longest location name. The longest place in src/data/locations.json is 85
 * characters, and 126 with its region and country added, which is the shape
 * the location search returns ("name, region, country").
 */
export const LOCATION_NAME_MAX = 150;

/**
 * The address shape the browser's type="email" field accepts (WHATWG HTML),
 * except the domain needs at least one dot. One @, and no spaces, control
 * characters, quotes, commas, semicolons or angle brackets, so the value is
 * one plain address and can't carry a second recipient or a header.
 */
const EMAIL_PATTERN =
  /^[A-Za-z0-9.!#$%&'*+/=?^_`{|}~-]+@[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?)+$/;

/** A link or markup in a location name: any "scheme://", "www.", or < >. */
const LINK_PATTERN = /:\/\/|www\.|[<>]/i;
const CONTROL_PATTERN = /\p{Cc}/u;

export function isValidEmail(value: unknown): value is string {
  if (typeof value !== 'string' || value.length > EMAIL_MAX) return false;
  if (!EMAIL_PATTERN.test(value)) return false;
  return value.indexOf('@') <= EMAIL_LOCAL_MAX;
}

export type ContactField = 'name' | 'email' | 'subject' | 'message';

export type ContactCheck =
  | { ok: true; value: { name: string; email: string; subject: string; message: string } }
  | { ok: false; field: ContactField; error: string };

export type SharedCalendarCheck =
  | { ok: true; value: { email: string; locationName: string } }
  | { ok: false; error: string };

const tooLong = (label: string, max: number) =>
  `${label} must be ${max.toLocaleString('en-US')} characters or fewer.`;

/** A trimmed string, '' for a missing value, or null for anything else. */
function text(value: unknown): string | null {
  if (value === undefined || value === null) return '';
  return typeof value === 'string' ? value.trim() : null;
}

/** The contact form body. On success, the trimmed fields to send. */
export function checkContactInput(body: unknown): ContactCheck {
  const b = (body !== null && typeof body === 'object' && !Array.isArray(body) ? body : {}) as Record<
    string,
    unknown
  >;

  const email = text(b.email);
  if (!email || !isValidEmail(email)) {
    return { ok: false, field: 'email', error: 'Please enter a valid email address.' };
  }

  const name = text(b.name);
  if (!name) return { ok: false, field: 'name', error: 'Please enter your name.' };
  if (name.length > CONTACT_LIMITS.name) {
    return { ok: false, field: 'name', error: tooLong('Name', CONTACT_LIMITS.name) };
  }

  const subject = text(b.subject);
  if (subject === null) return { ok: false, field: 'subject', error: 'Please enter a subject.' };
  if (subject.length > CONTACT_LIMITS.subject) {
    return { ok: false, field: 'subject', error: tooLong('Subject', CONTACT_LIMITS.subject) };
  }

  const message = text(b.message);
  if (!message) return { ok: false, field: 'message', error: 'Please enter a message.' };
  if (message.length > CONTACT_LIMITS.message) {
    return { ok: false, field: 'message', error: tooLong('Message', CONTACT_LIMITS.message) };
  }

  return { ok: true, value: { name, email, subject, message } };
}

/**
 * The address and location name for a shared calendar. The name is stored,
 * put in the email, and shown on the public calendar page, so it has to be
 * one short line of plain text with no link in it.
 */
export function checkSharedCalendarInput(input: { email: unknown; locationName: unknown }): SharedCalendarCheck {
  const email = text(input.email);
  if (!email || !isValidEmail(email)) return { ok: false, error: 'Invalid email format' };

  const locationName = text(input.locationName);
  if (!locationName) return { ok: false, error: 'Location name is required.' };
  if (locationName.length > LOCATION_NAME_MAX) {
    return { ok: false, error: tooLong('Location name', LOCATION_NAME_MAX) };
  }
  if (CONTROL_PATTERN.test(locationName)) {
    return { ok: false, error: 'Location name must be one line of plain text.' };
  }
  if (LINK_PATTERN.test(locationName)) {
    return { ok: false, error: "Location name can't contain a link or < >." };
  }

  return { ok: true, value: { email, locationName } };
}
